// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// compassFilterParse — Compass (#191) raw-filter-box parser.
//
// Turns the text a user types into the Compass "raw filter" box into a plain
// JS object suitable to send as a Mongo `find()` filter (Mongo backend) or to
// apply client-side (Convex backend). Pure + side-effect-free so it can be
// unit-tested against the exact behavior the UI relies on.
//
// Acceptance, in order:
//   1. Empty / whitespace            → { ok: true, filter: {}, relaxed: false }
//   2. Strict JSON object            → parsed as-is
//   3. Relaxed (JS-literal-ish):     → single-quoted strings and unquoted
//      object keys are normalized to JSON, then parsed. Flagged relaxed:true
//      so the UI can show "interpreted as" feedback.
//   4. Anything else (array, scalar, unparseable) → { ok: false, error }
//
// We deliberately do NOT eval the text — no `new Function`, no `eval`. Even
// though Compass is admin-only and runs in the admin's own browser, a pure
// transform keeps the blast radius at zero and the behavior testable.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

const EXAMPLE = '{ "statusCode": "4" }';

// Convert a forgiving JS-object-literal-ish string into strict JSON text.
// Handles: single-quoted strings → double-quoted; bare object keys → quoted.
// Intentionally conservative — if it can't confidently normalize, the strict
// parse below will just fail and the caller gets a clear error.
function relaxToJson(text) {
  let out = "";
  let i = 0;
  const n = text.length;
  while (i < n) {
    const ch = text[i];
    // Pass through a double-quoted string verbatim (respect escapes).
    if (ch === '"') {
      out += ch;
      i++;
      while (i < n) {
        out += text[i];
        if (text[i] === "\\") {
          // copy the escaped char too
          i++;
          if (i < n) out += text[i];
          i++;
          continue;
        }
        if (text[i] === '"') {
          i++;
          break;
        }
        i++;
      }
      continue;
    }
    // Convert a single-quoted string into a double-quoted one.
    if (ch === "'") {
      out += '"';
      i++;
      while (i < n) {
        const c = text[i];
        if (c === "\\") {
          out += c;
          i++;
          if (i < n) out += text[i];
          i++;
          continue;
        }
        if (c === "'") {
          out += '"';
          i++;
          break;
        }
        // Escape a double quote that appeared inside the single-quoted string.
        if (c === '"') {
          out += '\\"';
          i++;
          continue;
        }
        out += c;
        i++;
      }
      continue;
    }
    out += ch;
    i++;
  }

  // Quote bare object keys: `{ foo: 1, bar.baz: 2 }` → `{ "foo": 1, "bar.baz": 2 }`.
  // Only matches a key that follows `{` or `,` and precedes `:`. Keys already
  // in double quotes are left alone (the negative lookahead for `"`).
  out = out.replace(
    /([{,]\s*)(?!")([A-Za-z_$][\w$.]*)\s*:/g,
    '$1"$2":'
  );
  return out;
}

function isPlainObject(value) {
  return (
    value !== null && typeof value === "object" && !Array.isArray(value)
  );
}

export function parseCompassFilter(text) {
  const trimmed = (text || "").trim();
  if (trimmed === "") {
    return { ok: true, filter: {}, relaxed: false };
  }

  // 1) Strict JSON.
  try {
    const parsed = JSON.parse(trimmed);
    if (isPlainObject(parsed)) {
      return { ok: true, filter: parsed, relaxed: false };
    }
    return {
      ok: false,
      error: `Filter must be a JSON object, e.g. ${EXAMPLE}`,
    };
  } catch (_strictErr) {
    // fall through to relaxed
  }

  // 2) Relaxed.
  try {
    const relaxedText = relaxToJson(trimmed);
    const parsed = JSON.parse(relaxedText);
    if (isPlainObject(parsed)) {
      return { ok: true, filter: parsed, relaxed: true };
    }
    return {
      ok: false,
      error: `Filter must be a JSON object, e.g. ${EXAMPLE}`,
    };
  } catch (relaxedErr) {
    return {
      ok: false,
      error: `Could not parse filter as JSON. ${relaxedErr.message}. Example: ${EXAMPLE}`,
    };
  }
}
